import { createFileRoute, redirect } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Plus,
  Building2,
  CreditCard,
  Wallet,
  Smartphone,
  Edit,
  Trash2,
  ArrowLeft,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useAccounts, useDeleteAccount } from '@/hooks/queries/useTreasury';
import { AccountForm } from '@/features/treasury/components/AccountForm';
import { useState } from 'react';
import { toast } from '@/components/ui/toast/useToast';
import type { Account } from '@/types/treasury';
import { ACCOUNT_TYPES } from '@/types/treasury';

export const Route = createFileRoute('/apps/treasury/accounts/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: AccountsPage,
});

function AccountsPage() {
  const { company } = useAuthStore();
  const { data: accounts = [], isLoading, refetch } = useAccounts(company?.id || '');
  const deleteAccount = useDeleteAccount();
  
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | undefined>();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleEdit = (account: Account) => {
    setEditingAccount(account);
    setShowAccountForm(true);
  };

  const handleDelete = async (account: Account) => {
    if (confirm(`Êtes-vous sûr de vouloir supprimer le compte "${account.name}"?`)) {
      try {
        await deleteAccount.mutateAsync(account.id);
        toast.success('Compte supprimé', 'Le compte a été supprimé avec succès.');
        refetch();
      } catch (error) {
        toast.error('Erreur', 'Impossible de supprimer le compte.');
      }
    }
  };

  const handleCloseForm = () => {
    setShowAccountForm(false);
    setEditingAccount(undefined);
    refetch(); // Refresh the list
  };

  const getAccountIcon = (type: string) => {
    switch (type) {
      case 'bank':
        return <Building2 className="w-6 h-6" />;
      case 'ccp':
        return <CreditCard className="w-6 h-6" />;
      case 'cash':
        return <Wallet className="w-6 h-6" />;
      case 'mobile_money':
        return <Smartphone className="w-6 h-6" />;
      default:
        return <Building2 className="w-6 h-6" />;
    }
  };

  const getAccountColor = (type: string) => {
    const colors = {
      bank: 'bg-blue-50 text-blue-600',
      ccp: 'bg-green-50 text-green-600',
      cash: 'bg-purple-50 text-purple-600',
      mobile_money: 'bg-orange-50 text-orange-600',
    };
    return colors[type as keyof typeof colors] || colors.bank;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.history.back()}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              الحسابات / Comptes
            </h1>
            <p className="mt-2 text-gray-600">
              إدارة جميع حساباتك المالية / Gérez tous vos comptes financiers
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => refetch()}
            >
              تحديث / Actualiser
            </Button>
            <Button
              onClick={() => {
                setEditingAccount(undefined);
                setShowAccountForm(true);
              }}
              className="bg-gradient-to-r from-emerald-500 to-teal-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              حساب جديد / Nouveau compte
            </Button>
          </div>
        </div>

        {/* Accounts Grid */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Chargement...</p>
          </div>
        ) : accounts.length === 0 ? (
          <Card className="border-2 border-dashed border-gray-300">
            <CardContent className="p-12 text-center">
              <CreditCard className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucun compte encore
              </h3>
              <p className="text-gray-600 mb-6">
                Créez votre premier compte pour commencer
              </p>
              <Button
                onClick={() => setShowAccountForm(true)}
                className="bg-gradient-to-r from-emerald-500 to-teal-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Créer un compte
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {accounts.map((account) => {
              const accountType = ACCOUNT_TYPES[account.type];
              
              return (
                <Card key={account.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className={`p-3 rounded-xl ${getAccountColor(account.type)}`}>
                        {getAccountIcon(account.type)}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(account)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(account)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900 mb-1">
                        {account.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {accountType.labelAr} / {accountType.label}
                      </p>
                    </div>

                    <div className="pt-3 border-t">
                      <p className="text-sm text-gray-600 mb-1">Solde actuel</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(account.current_balance)}
                      </p>
                    </div>

                    {/* Account Details */}
                    <div className="space-y-2 text-sm">
                      {account.account_number && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">N° Compte:</span>
                          <span className="font-medium">{account.account_number}</span>
                        </div>
                      )}
                      
                      {account.type === 'bank' && account.bank_name && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Banque:</span>
                          <span className="font-medium text-sm">{account.bank_name.split('-')[0].trim()}</span>
                        </div>
                      )}
                      
                      {account.type === 'ccp' && account.ccp_number && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">CCP:</span>
                          <span className="font-medium">
                            {account.ccp_number} {account.ccp_key}
                          </span>
                        </div>
                      )}
                      
                      {account.type === 'mobile_money' && account.mobile_number && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tél:</span>
                          <span className="font-medium">{account.mobile_number}</span>
                        </div>
                      )}
                    </div>

                    {/* Status Badges */}
                    <div className="flex gap-2 pt-2">
                      {account.is_default && (
                        <span className="px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">
                          Par défaut
                        </span>
                      )}
                      {account.is_active ? (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                          Actif
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                          Inactif
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Account Form Dialog */}
        <AccountForm
          open={showAccountForm}
          onClose={handleCloseForm}
          account={editingAccount}
        />
      </div>
    </DashboardLayout>
  );
}
